import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import {
  calcSalesUnitHourlyRates,
  type SalesUnitHourlyRateRow,
} from "@/lib/sales-unit-hourly-rate";
import type { DashboardPeriod } from "@/lib/dashboard-period";

interface RawSalesUnit {
  id: string;
  name: string;
}

interface RawProject {
  id: string;
  name: string;
  project_code: string | null;
  completed_at: string | null;
  bf_actual: number | null;
}

interface RawOwner {
  project_id: string;
  sales_unit_id: string | null;
  ownership_pct: number | null;
}

interface RawTimeEntry {
  project_id: string | null;
  duration_seconds: number | null;
}

export function useSalesUnitHourlyRates(period: DashboardPeriod, enabled = true) {
  return useQuery({
    queryKey: ["sales-unit-hourly-rates", period],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SalesUnitHourlyRateRow[]> => {
      const [salesUnits, projects] = await Promise.all([
        fetchAllPaginated<RawSalesUnit>(
          "sales_units",
          "id, name",
          (query) => query.eq("year", period.year),
        ),
        fetchAllPaginated<RawProject>(
          "projects",
          "id, name, project_code, completed_at, bf_actual",
          (query) => query
            .eq("project_status", "completed")
            .is("deleted_at", null)
            .not("completed_at", "is", null),
        ),
      ]);

      const projectIds = projects.map((project) => project.id);
      const [owners, timeEntries] = projectIds.length === 0
        ? [[], []] as [RawOwner[], RawTimeEntry[]]
        : await Promise.all([
            fetchAllPaginated<RawOwner>(
              "project_owners",
              "project_id, sales_unit_id, ownership_pct",
              (query) => query.in("project_id", projectIds).not("sales_unit_id", "is", null),
            ),
            fetchAllPaginated<RawTimeEntry>(
              "time_entries",
              "project_id, duration_seconds",
              (query) => query.in("project_id", projectIds).is("deleted_at", null),
            ),
          ]);

      return calcSalesUnitHourlyRates({
        period,
        salesUnits,
        projects: projects.map((project) => ({
          id: project.id,
          name: project.name,
          projectCode: project.project_code,
          completedAt: project.completed_at,
          bfActual: project.bf_actual,
        })),
        owners: owners.map((owner) => ({
          projectId: owner.project_id,
          salesUnitId: owner.sales_unit_id,
          ownershipPct: owner.ownership_pct,
        })),
        timeEntries: timeEntries.map((entry) => ({
          projectId: entry.project_id,
          durationSeconds: entry.duration_seconds,
        })),
      });
    },
  });
}
